import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../commonDesign/Button'
import { COLUMN_CATALOG_MAP, createColumnInstance } from '../lib/constants'
import useColumnBuilderStyles from './columnBuilderStyles'
import CatalogPanel from './CatalogPanel'
import DraggableColumnRow from './DraggableColumnRow'
import ColumnDetailPanel from './ColumnDetailPanel'

export default function ColumnBuilderDialog ({ open, onClose, currentColumns, onApply }) {
  const classes = useColumnBuilderStyles()
  const [columns, setColumns] = useState(currentColumns || [])
  const [selectedColumnId, setSelectedColumnId] = useState(null)

  useEffect(() => {
    if (open) {
      setColumns(currentColumns || [])
      setSelectedColumnId(null)
    }
  }, [open, currentColumns])

  const selectedColumn = useMemo(
    () => columns.find(c => c.id === selectedColumnId) || null,
    [columns, selectedColumnId]
  )

  const handleAddColumn = useCallback((columnType) => {
    const instance = createColumnInstance(columnType)
    if (!instance) return
    const cat = COLUMN_CATALOG_MAP[columnType]
    if (cat?.kind === 'window') {
      setColumns(prev => {
        const firstCurrency = prev.find(c => {
          const cc = COLUMN_CATALOG_MAP[c.columnType]
          return cc && cc.kind !== 'window' && cc.kind !== 'derived' && cc.kind !== 'comparison' && cc.measureKind === 'currency'
        })
        const bound = firstCurrency
          ? { ...instance, options: { ...instance.options, sourceColumnId: firstCurrency.id, sourceColumnType: firstCurrency.columnType } }
          : instance
        return [...prev, bound]
      })
    } else if (cat?.kind === 'comparison') {
      setColumns(prev => {
        const currencyCols = prev.filter(c => {
          const cc = COLUMN_CATALOG_MAP[c.columnType]
          return cc && cc.kind !== 'window' && cc.kind !== 'derived' && cc.kind !== 'comparison' && cc.measureKind === 'currency'
        })
        const bound = { ...instance, options: { ...instance.options } }
        if (currencyCols[0]) { bound.options.columnAId = currencyCols[0].id; bound.options.columnAType = currencyCols[0].columnType }
        if (currencyCols[1]) { bound.options.columnBId = currencyCols[1].id; bound.options.columnBType = currencyCols[1].columnType }
        return [...prev, bound]
      })
    } else {
      setColumns(prev => [...prev, instance])
    }
    setSelectedColumnId(instance.id)
  }, [])

  const handleRemove = useCallback((id) => {
    setColumns(prev => prev.filter(c => c.id !== id))
    setSelectedColumnId(cur => cur === id ? null : cur)
  }, [])

  const handleOptionChange = useCallback((id, optionPatch) => {
    setColumns(prev => prev.map(c =>
      c.id === id ? { ...c, options: { ...c.options, ...optionPatch } } : c
    ))
  }, [])

  const handleLabelChange = useCallback((id, label) => {
    setColumns(prev => prev.map(c =>
      c.id === id ? { ...c, label: label || null } : c
    ))
  }, [])

  const moveItem = useCallback((fromIndex, toIndex) => {
    setColumns(prev => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply(columns)
    onClose()
  }, [columns, onApply, onClose])

  return (
    <SydModal open={open} onClose={onClose} title='Column Builder' size='large'>
      <div className={classes.dialogLayout}>
        <DndProvider backend={HTML5Backend}>
          <div className={classes.body}>
            <CatalogPanel onAddColumn={handleAddColumn} />

            <div className={classes.columnPanel}>
              <div className={classes.panelHeader}>Columns (drag to reorder)</div>
              <div className={classes.columnList}>
                {columns.length === 0 ? (
                  <div className={classes.emptyHint}>Click a column from the left to add it</div>
                ) : null}
                {columns.map((col, index) => (
                  <DraggableColumnRow
                    key={col.id}
                    column={col}
                    index={index}
                    isSelected={col.id === selectedColumnId}
                    moveItem={moveItem}
                    onSelect={setSelectedColumnId}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </div>

            <ColumnDetailPanel
              column={selectedColumn}
              allColumns={columns}
              onOptionChange={handleOptionChange}
              onLabelChange={handleLabelChange}
            />
          </div>
        </DndProvider>
        <SydModalActions>
          <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
          <SydButton variant='primary' size='sm' onClick={handleApply} disabled={columns.length === 0}>Apply</SydButton>
        </SydModalActions>
      </div>
    </SydModal>
  )
}

ColumnBuilderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentColumns: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired
}
